import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../Firebase';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { 
  Plus, Edit2, Trash2, Save, X, Search, Briefcase, MapPin, Clock, 
  User, Mail, Phone, FileText, CheckCircle2, FileUp, AlertCircle, Calendar,
  Download, ExternalLink, Users
} from 'lucide-react';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { base64ToBlobUrl } from '../utils/fileHelper';

const JobManager = () => {
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' or 'applications'
  
  // Jobs State
  const [jobs, setJobs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  
  // Applications State
  const [applications, setApplications] = useState([]);
  const [appSearchTerm, setAppSearchTerm] = useState('');

  const [loading, setLoading] = useState(false);

  // Resume Modal State
  const [selectedResume, setSelectedResume] = useState(null); // { url: '', name: '' }

  // Cleanup Object URL on unmount
  useEffect(() => {
    return () => {
      if (selectedResume?.url) {
        URL.revokeObjectURL(selectedResume.url);
      }
    };
  }, [selectedResume]);

  useEffect(() => {
    if (isModalOpen || selectedResume) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen, selectedResume]);

  const handleViewResume = (base64String, name) => {
    if (selectedResume?.url) {
      URL.revokeObjectURL(selectedResume.url);
    }

    const blobUrl = base64ToBlobUrl(base64String);
    if (blobUrl) {
      setSelectedResume({ url: blobUrl, name });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate preview for this resume.',
        confirmButtonColor: '#EF4444'
      });
    }
  };

  const handleCloseResumeModal = () => {
    if (selectedResume?.url) {
      URL.revokeObjectURL(selectedResume.url);
    }
    setSelectedResume(null);
  };

  const [formData, setFormData] = useState({
    title: '',
    type: 'Full Time',
    location: '',
    experience: '',
    category: '',
    description: '',
    vacancies: ''
  });

  // Fetch Jobs
  useEffect(() => {
    const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setJobs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  // Fetch Applications
  useEffect(() => {
    const q = query(collection(db, 'job_applications'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setApplications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'Full Time',
      location: '',
      experience: '',
      category: '',
      description: '',
      vacancies: ''
    });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const jobData = {
        ...formData,
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, 'jobs', editingId), jobData);
        Swal.fire('Updated!', 'Job posting updated successfully.', 'success');
      } else {
        jobData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'jobs'), jobData);
        Swal.fire('Created!', 'New job posting created successfully.', 'success');
      }
      resetForm();
    } catch (error) {
      console.error("Error saving job:", error);
      Swal.fire('Error', 'Failed to save job posting.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditJob = (job) => {
    setFormData({
      title: job.title || '',
      type: job.type || 'Full Time',
      location: job.location || '',
      experience: job.experience || '',
      category: job.category || '',
      description: job.description || '',
      vacancies: job.vacancies || ''
    });
    setEditingId(job.id);
    setIsModalOpen(true);
  };

  const handleDeleteJob = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This job posting will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'jobs', id));
        Swal.fire('Deleted!', 'Job posting has been deleted.', 'success');
      } catch (error) {
        console.error("Error deleting job:", error);
        Swal.fire('Error', 'Failed to delete job.', 'error');
      }
    }
  };

  const handleUpdateAppStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Pending' ? 'Reviewed' : 'Pending';
      await updateDoc(doc(db, 'job_applications', id), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Application marked as ${newStatus}`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleDeleteApplication = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This job application will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'job_applications', id));
        Swal.fire('Deleted!', 'Job application has been deleted.', 'success');
      } catch (error) {
        console.error("Error deleting job application:", error);
        Swal.fire('Error', 'Failed to delete job application.', 'error');
      }
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(jobSearchTerm.toLowerCase()) || 
    job.category.toLowerCase().includes(jobSearchTerm.toLowerCase())
  );

  const filteredApps = applications.filter(app => 
    app.name?.toLowerCase().includes(appSearchTerm.toLowerCase()) || 
    app.position?.toLowerCase().includes(appSearchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Job Postings & Inquiries</h1>
          <p className="text-gray-500 font-medium">Manage careers opportunities and review applicant resumes.</p>
        </div>
        
        <div className="flex bg-white rounded-2xl p-1 shadow-sm border border-gray-100 w-max">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
              activeTab === 'jobs' ? 'bg-primary text-white shadow-md' : 'text-gray-500 hover:text-primary'
            }`}
          >
            Manage Jobs
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
              activeTab === 'applications' ? 'bg-primary text-white shadow-md' : 'text-gray-500 hover:text-primary'
            }`}
          >
            Job Inquiries
            {applications.filter(a => a.status === 'Pending').length > 0 && (
              <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">
                {applications.filter(a => a.status === 'Pending').length}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'jobs' ? (
        <div className="space-y-6 animate-in fade-in">
          {/* Jobs Toolbar */}
          <div className="flex justify-between items-center bg-white p-4 rounded-[2rem] border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-2.5 rounded-xl w-72 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <Search className="w-5 h-5 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search jobs..." 
                value={jobSearchTerm}
                onChange={(e) => setJobSearchTerm(e.target.value)}
                className="bg-transparent border-none outline-none text-sm text-gray-700 w-full"
              />
            </div>
            <button 
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              className="btn-primary flex items-center gap-2 px-6 py-2.5 shadow-lg shadow-primary/30"
            >
              <Plus className="w-5 h-5" />
              Post New Job
            </button>
          </div>

          {/* Jobs List */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-all flex flex-col group"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-secondary/10 text-primary-dark rounded-full text-[10px] font-bold uppercase tracking-widest">
                        {job.category}
                      </span>
                      <span className="flex items-center gap-1 text-gray-400 text-[10px] font-bold uppercase tracking-widest">
                        <Clock className="w-3 h-3" /> {job.type}
                      </span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEditJob(job)} className="p-2 text-gray-400 hover:text-primary bg-gray-50 rounded-lg hover:bg-primary/10">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteJob(job.id)} className="p-2 text-gray-400 hover:text-red-500 bg-gray-50 rounded-lg hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-primary-dark mb-2">{job.title}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-4 flex-grow">{job.description}</p>
                  
                  <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-50 mt-auto">
                    <div className="flex items-center gap-2 text-gray-400 font-bold text-xs">
                      <MapPin className="w-4 h-4 text-secondary" /> {job.location}
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 font-bold text-xs">
                      <Briefcase className="w-4 h-4 text-secondary" /> {job.experience}
                    </div>
                    {job.vacancies && (
                      <div className="flex items-center gap-2 text-gray-400 font-bold text-xs">
                        <Users className="w-4 h-4 text-secondary" /> {job.vacancies} Vacancies
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {filteredJobs.length === 0 && (
              <div className="col-span-full py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-500">No jobs posted yet</h3>
                <p className="text-gray-400 mt-2">Click "Post New Job" to add a career opportunity.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6 animate-in fade-in">
          {/* Applications Toolbar */}
          <div className="flex justify-between items-center bg-white p-4 rounded-[2rem] border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-2.5 rounded-xl w-72 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <Search className="w-5 h-5 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search applicants..." 
                value={appSearchTerm}
                onChange={(e) => setAppSearchTerm(e.target.value)}
                className="bg-transparent border-none outline-none text-sm text-gray-700 w-full"
              />
            </div>
            <div className="text-sm font-bold text-gray-500">
              Total Inquiries: <span className="text-primary">{filteredApps.length}</span>
            </div>
          </div>

          {/* Applications List */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AnimatePresence>
              {filteredApps.map((app, index) => (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all"
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-primary/5 text-primary rounded-2xl flex items-center justify-center border border-primary/10">
                          <User className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-primary-dark">{app.name}</h3>
                          <span className="text-xs font-bold text-secondary uppercase tracking-widest">{app.position}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUpdateAppStatus(app.id, app.status)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                          app.status === 'Reviewed' || app.status === 'Done'
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 hover:bg-emerald-100' 
                          : 'bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100'
                        }`}
                      >
                        {app.status === 'Reviewed' || app.status === 'Done' ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                        {app.status === 'Reviewed' || app.status === 'Done' ? 'Reviewed' : 'Pending Review'}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="flex items-center gap-3 text-gray-600">
                        <div className="p-2 bg-gray-50 rounded-lg"><Mail className="w-4 h-4" /></div>
                        <span className="text-sm font-medium truncate">{app.email}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <div className="p-2 bg-gray-50 rounded-lg"><Phone className="w-4 h-4" /></div>
                        <span className="text-sm font-medium">{app.phone}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <div className="p-2 bg-gray-50 rounded-lg"><MapPin className="w-4 h-4" /></div>
                        <span className="text-sm font-medium">{app.city}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <div className="p-2 bg-gray-50 rounded-lg"><Briefcase className="w-4 h-4" /></div>
                        <span className="text-sm font-medium">{app.experience}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                      <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                        <Calendar className="w-4 h-4" />
                        {app.createdAt?.toDate().toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </div>
                      <div className="flex items-center gap-2">
                        {app.resumeUrl ? (
                          <button 
                            onClick={() => handleViewResume(app.resumeUrl, app.name)}
                            className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-lg font-bold text-sm hover:bg-primary hover:text-white transition-all"
                          >
                            <FileUp className="w-4 h-4" />
                            View Resume
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm font-medium italic mr-2">No Resume Uploaded</span>
                        )}
                        <button
                          onClick={() => handleDeleteApplication(app.id)}
                          className="p-2 rounded-lg bg-red-50 text-red-600 border border-red-100 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm transition-all"
                          title="Delete Inquiry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {filteredApps.length === 0 && (
              <div className="col-span-full py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-500">No inquiries found</h3>
                <p className="text-gray-400 mt-2">There are currently no job applications.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create / Edit Job Modal */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[2.5rem] p-8 max-w-2xl w-full my-8 shadow-2xl relative"
          >
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 p-2 bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary-dark">
                {editingId ? 'Edit Job Posting' : 'Post New Job'}
              </h2>
              <p className="text-gray-500">Add details for the career opportunity.</p>
            </div>

            <form onSubmit={handleJobSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Job Title</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g. Relationship Manager"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Job Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                  >
                    <option value="Full Time">Full Time</option>
                    <option value="Part Time">Part Time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Category</label>
                  <input
                    type="text"
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g. Banking, Management..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Location</label>
                  <input
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="e.g. Pune, Main Branch"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Experience</label>
                  <input
                    type="text"
                    required
                    value={formData.experience}
                    onChange={(e) => setFormData({...formData, experience: e.target.value})}
                    placeholder="e.g. 3-5 Years"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Vacancies</label>
                  <input
                    type="text"
                    required
                    value={formData.vacancies}
                    onChange={(e) => setFormData({...formData, vacancies: e.target.value})}
                    placeholder="e.g. 2"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-gray-700 uppercase tracking-widest">Description</label>
                  <textarea
                    required
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of the role and responsibilities..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium resize-none"
                  ></textarea>
                </div>
              </div>

              <div className="pt-6 flex justify-end gap-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary px-8 flex items-center gap-2"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
                  {editingId ? 'Update Job' : 'Post Job'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Resume PDF Viewer Modal */}
      {selectedResume && createPortal(
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-[2.5rem] overflow-hidden max-w-5xl w-full shadow-2xl flex flex-col h-[90vh] relative border border-gray-100"
          >
            {/* Modal Header/Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 bg-gray-50 border-b border-gray-100">
              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-secondary bg-secondary/10 px-3 py-1 rounded-full">
                  Resume Preview
                </span>
                <h3 className="text-xl font-bold text-primary-dark mt-1 truncate max-w-sm sm:max-w-md">
                  {selectedResume.name}'s Resume
                </h3>
              </div>

              {/* Toolbar Actions */}
              <div className="flex items-center gap-3">
                <a
                  href={selectedResume.url}
                  download={`Resume_${selectedResume.name.replace(/\s+/g, '_')}.pdf`}
                  className="flex items-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Download PDF</span>
                </a>
                <a
                  href={selectedResume.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-primary/5 text-primary hover:bg-primary hover:text-white px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Open in New Tab</span>
                </a>
                <button
                  onClick={handleCloseResumeModal}
                  className="p-2.5 bg-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all"
                  aria-label="Close Preview"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal Body: PDF Iframe */}
            <div className="flex-grow bg-gray-100 p-2 relative flex items-center justify-center">
              <iframe
                src={selectedResume.url}
                className="w-full h-full rounded-2xl border-none shadow-inner"
                title={`${selectedResume.name}'s Resume Preview`}
              />
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JobManager;
